import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { EventCard } from "@/components/EventCard";
import { WaitlistForm } from "@/components/WaitlistForm";
import { Marquee } from "@/components/Marquee";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

export default function IrlPage() {
  useSEO("Roamies IRL — Local Social Events for Solo People", "Dinners with strangers, rooftop socials, beach days. Meet the Roamies community in your city before committing to a bigger trip.");
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const formRef = useRef(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  const onRsvp = (event) => {
    setSelected(event);
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const upcoming = events.filter((e) => e.status !== "completed");

  return (
    <div data-testid="irl-page">
      <section className="pt-16 sm:pt-24 pb-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">
            Roamies IRL
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.9]" data-testid="irl-heading">
            Small events.<br />Big energy.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-6 max-w-xl text-ink/70 leading-relaxed">
            Roamies isn't only multi-day trips. IRL is how solo people meet the community first — a dinner, a rooftop, a beach day in your city. Come to one, and the weekend trip stops feeling like a leap.
          </motion.p>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <p className="text-ink/50 font-semibold py-16" data-testid="irl-loading">Loading events...</p>
          ) : upcoming.length === 0 ? (
            <div className="bg-white border border-black/10 p-10 max-w-xl" data-testid="irl-empty">
              <p className="font-display text-3xl uppercase">Your city is next.</p>
              <p className="mt-3 text-sm text-ink/65 leading-relaxed">
                First IRL events are being planned. Join the waitlist below and tell us your city — that's how we pick where to go first.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcoming.map((ev, i) => (
                <motion.div key={ev.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }}>
                  <EventCard event={ev} onRsvp={onRsvp} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Marquee />

      <section ref={formRef} className="bg-ink text-cream noise-bg py-20 sm:py-28" data-testid="irl-waitlist-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">
              {selected ? `RSVP — ${selected.title}` : "Get Invited"}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">
              {selected ? `See you in ${selected.city}.` : "Get your city's first invite."}
            </h2>
            <p className="mt-6 text-cream/70 max-w-md leading-relaxed">
              {selected
                ? `${selected.date_label || "Date TBA"}${selected.time_label ? ` · ${selected.time_label}` : ""} · ${selected.price_label || "Free"} · ${selected.age_req}. Leave your details and we'll confirm your spot.`
                : "Tell us your city and we'll make sure you're first to know when a Roamies IRL lands there."}
            </p>
            {selected?.meeting_instructions && (
              <p className="mt-4 text-xs text-cream/50">Meeting instructions are shared with confirmed attendees.</p>
            )}
          </div>
          <div className="bg-cream text-ink p-6 sm:p-10">
            <WaitlistForm
              key={selected?.slug || "general"}
              trips={[]}
              events={events}
              initialTrip={selected ? `irl:${selected.slug}` : ""}
              lockTrip={!!selected}
              heading={selected ? "RSVP" : "Join the IRL list"}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
